import {
    CustomListItemId,
    CustomListKey,
    EVERYONE_SENTINEL,
    ILocation,
    locationToCoordinates,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { HoveredMapEntity } from "components/community-search/community-map/types";
import { SearchGeoParamsCallout } from "components/community-search/search-bar/search-geo-params-callout";
import { useDynamicCalloutPosition } from "components/community-search/search-bar/use-dynamic-callout-position";
import { HomeCareClientsPage } from "components/feature";
import { Content, PageHeader, Stage } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { useDebouncedState } from "components/shared/hooks";
import { SelectableUser } from "components/shared/user-select";
import { ViewMapButton } from "components/shared/view-map-button";
import { SearchRequestPagination } from "components/simple-search/search-request-pagination";
import { defaultLinkStyle } from "components/ui/link";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { SearchGeoParams } from "generated/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { ResponsiveMode } from "store/reducers/responsive";
import { useCustomList } from "store/selectors/bridge";
import { useRegionId, useResponsiveMode } from "store/selectors/hooks";
import { clientMapViewSlice } from "store/slices/client-map-view";
import { ClientMapCardList } from "./client-map-card-list";
import { ClientMapSearchResultsMap } from "./client-map-search-results-map";
import { ClientMapEmployeeFilter } from "./employee-filter";
import { ClientMapSearchParams, toGeoParamsInput } from "./geo-params";
import { ClientMapSearchInput, ClientMapSearchType } from "./search-input";
import { ClientStatusFilter } from "./status-filter";
import { statusIdsForSelection } from "./status-groups";

const clientMapThreshold: ResponsiveMode = ResponsiveMode.xLarge;

const ClientMapViewInner: React.FC<unknown> = () => {
    const regionId = useRegionId();
    const { customList } = useCustomList(CustomListKey.HomeCareLeadStatus);

    const dispatch = useAppDispatch();
    const clientMapView = useAppSelector(state => state.clientMapView);
    const {
        updateSearchText,
        updateSearchType,
        updateAssignedUserId,
        updateSelectedStatusIds,
        updateGeoParams,
        setExplicitMapBounds,
        resetMapBounds,
        changePage,
        changePageSize,
        clearAllFilters,
    } = clientMapViewSlice.actions;
    const {
        searchText,
        searchType,
        assignedUserId,
        selectedStatusIds,
        geoParams,
        page,
        perPage,
    } = clientMapView;

    const [searchTextInput, setSearchTextInput, searchTextDebounced] = useDebouncedState(
        searchText,
        (value: string) => (value.length < 3 ? 750 : 600),
    );
    const [hoveredClient, setHoveredClient] = useState<HoveredMapEntity | null>(null);
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [geoParamsRef, geoParamsPosition] = useDynamicCalloutPosition();

    useEffect(() => {
        if (searchTextDebounced !== searchText) {
            dispatch(updateSearchText(searchTextDebounced));
        }
    }, [searchTextDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

    const statusListItemIds = useMemo(
        () => statusIdsForSelection(customList, selectedStatusIds),
        [customList, selectedStatusIds],
    );

    const searchParams: ClientMapSearchParams = useMemo(
        () => ({
            searchString: searchText,
            searchType,
            assignedUserId: assignedUserId === EVERYONE_SENTINEL ? null : assignedUserId,
            statusListItemIds,
            geoParams,
        }),
        [searchText, searchType, assignedUserId, statusListItemIds, geoParams],
    );

    const trpcClient = trpc.useTRPC();
    const listQuery = useQuery({
        ...trpcClient.homeCareLead.list.queryOptions({
            searchString: searchParams.searchString,
            searchType: searchParams.searchType,
            assignedUserId: searchParams.assignedUserId,
            statusListItemIds: searchParams.statusListItemIds,
            geoParams: toGeoParamsInput(searchParams.geoParams),
            regionId,
        }),
        enabled: !!customList,
    });

    const leads = useMemo(() => listQuery.data ?? [], [listQuery.data]);
    const pagedLeads = useMemo(
        () => leads.slice(page * perPage, page * perPage + perPage),
        [leads, page, perPage],
    );

    const handleExplicitBoundsChanged = useCallback(
        (northWest: ILocation, southEast: ILocation) => {
            dispatch(
                setExplicitMapBounds({
                    northWest: locationToCoordinates(northWest),
                    southEast: locationToCoordinates(southEast),
                }),
            );
        },
        [dispatch, setExplicitMapBounds],
    );

    const handleResetMapView = useCallback(() => {
        dispatch(resetMapBounds());
    }, [dispatch, resetMapBounds]);

    const handleGeoParamsChange = useCallback(
        (value: SearchGeoParams | null) => {
            dispatch(updateGeoParams(value));
        },
        [dispatch, updateGeoParams],
    );

    const showClearAllFilters =
        searchText !== "" ||
        assignedUserId !== EVERYONE_SENTINEL ||
        selectedStatusIds.length > 0 ||
        geoParams !== null;

    const handleClearAllFilters = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            setSearchTextInput("");
            dispatch(clearAllFilters());
        },
        [dispatch, clearAllFilters, setSearchTextInput],
    );

    const handleSearchTypeChange = useCallback(
        (value: ClientMapSearchType) => {
            dispatch(updateSearchType(value));
        },
        [dispatch, updateSearchType],
    );

    const handleAssignedUserIdChange = useCallback(
        (value: SelectableUser) => {
            dispatch(updateAssignedUserId(value));
        },
        [dispatch, updateAssignedUserId],
    );

    const handleSelectedStatusIdsChange = useCallback(
        (value: CustomListItemId[]) => {
            dispatch(updateSelectedStatusIds(value));
        },
        [dispatch, updateSelectedStatusIds],
    );

    const handlePageChange = useCallback(
        (value: number) => {
            dispatch(changePage(value));
        },
        [dispatch, changePage],
    );

    const handlePageSizeChange = useCallback(
        (value: number) => {
            dispatch(changePageSize(value));
        },
        [dispatch, changePageSize],
    );

    const handleOpenMap = useCallback(() => {
        setIsMapOpen(true);
    }, []);

    const handleCloseMap = useCallback(() => {
        setIsMapOpen(false);
    }, []);

    const responsiveMode = useResponsiveMode();
    const showMap = responsiveMode >= clientMapThreshold;

    const renderMap = () => (
        <ClientMapSearchResultsMap
            hoveredClient={hoveredClient}
            searchParams={searchParams}
            regionId={regionId}
            leads={leads}
            onExplicitBoundsChanged={handleExplicitBoundsChanged}
        />
    );

    return (
        <Stage>
            <PageHeader title="Home Care Client Map" />
            <Content>
                <div className="max-2xl:space-y-4 2xl:flex 2xl:items-end 2xl:space-x-4 action-bar">
                    <div className="flex flex-wrap gap-2 items-end">
                        <ClientMapSearchInput
                            searchText={searchTextInput}
                            onSearchTextChange={setSearchTextInput}
                            searchType={searchType}
                            onSearchTypeChange={handleSearchTypeChange}
                        />
                    </div>
                    <div className="gap-x-4 gap-y-2 flex flex-wrap items-center 2xl:pb-2">
                        <div ref={geoParamsRef}>
                            <SearchGeoParamsCallout
                                value={geoParams}
                                onChange={handleGeoParamsChange}
                                onResetMapView={handleResetMapView}
                                align={geoParamsPosition.align}
                                arrowPosition={geoParamsPosition.arrowPosition}
                                maxWidthPx={geoParamsPosition.maxWidthPx}
                                mobileFullWidth={geoParamsPosition.mobileFullWidth}
                            />
                        </div>
                        <ClientMapEmployeeFilter
                            value={assignedUserId}
                            onChange={handleAssignedUserIdChange}
                        />
                        <ClientStatusFilter
                            customList={customList}
                            value={selectedStatusIds}
                            onChange={handleSelectedStatusIdsChange}
                        />
                        {showClearAllFilters ? (
                            <a
                                href="#"
                                className={`${defaultLinkStyle} text-sm`}
                                onClick={handleClearAllFilters}>
                                Clear all filters
                            </a>
                        ) : null}
                    </div>
                </div>
                <div className="pb-20 lg:pb-0 mt-4">
                    <div className="w-full flex">
                        <div className="flex-1 mr-4">
                            <ClientMapCardList
                                leads={pagedLeads}
                                total={leads.length}
                                setHoveredClient={setHoveredClient}
                            />
                            <SearchRequestPagination
                                request={{
                                    page,
                                    perPage,
                                    sort: "",
                                    sortDirection: "ASC",
                                }}
                                total={leads.length}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        </div>
                        {showMap ? renderMap() : null}
                    </div>
                    {!showMap ? <ViewMapButton onClick={handleOpenMap} /> : null}
                    <ResponsivePanel
                        isOpen={isMapOpen}
                        type={PanelType.extraLarge}
                        headerText="Client Map View"
                        noPadding={true}
                        onDismiss={handleCloseMap}>
                        <div className="h-full">{renderMap()}</div>
                    </ResponsivePanel>
                </div>
            </Content>
        </Stage>
    );
};

export const ClientMapView: React.FC<unknown> = () => {
    return (
        <HomeCareClientsPage>
            <ClientMapViewInner />
        </HomeCareClientsPage>
    );
};
